import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShieldAlert, Pencil, BellRing, CheckCircle2 } from 'lucide-react';

const STATUS = {
  ok: { label: 'Reviewed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  due_soon: { label: 'Review due soon', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  overdue: { label: 'Review overdue', cls: 'bg-red-50 text-red-700 border-red-200' },
  not_reviewed: { label: 'No backup plan', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export const CriticalSkillsTab = ({ canManage, employees = [], onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyCritical, setOnlyCritical] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [empSearch, setEmpSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData((await hrAPI.criticalSkills()).data); }
    catch { toast.error('Failed to load critical skills'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = (data?.rows || []).filter(r => (onlyCritical ? r.critical : true));

  const save = async () => {
    setSaving(true);
    try {
      await hrAPI.saveCriticalSkillBackup(dlg.skill_id, {
        backup_employee_ids: dlg.backup_employee_ids,
        backup_plan: dlg.backup_plan,
        last_reviewed_date: dlg.last_reviewed_date || undefined,
      });
      toast.success('Backup plan saved — next review in 6 months');
      setDlg(null); setEmpSearch('');
      load(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const runCheck = async () => {
    try {
      const res = await hrAPI.runCriticalSkillReviewCheck();
      toast.success(`Review check: ${res.data.notified ?? 0} skill(s) notified, ${res.data.emails_sent ?? 0} email(s) sent`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to run review check'); }
  };

  const s = data?.summary;
  const empPool = employees.filter(e => (e.name || '').toLowerCase().includes(empSearch.toLowerCase()));

  return (
    <Card className="border border-slate-200 shadow-none"><CardContent className="p-4 space-y-3">
      <p className="text-sm text-slate-600">
        A skill is <b>critical</b> when fewer than 2 persons are at <b>S2 or above</b>, or a specific qualification is required.
        Every critical skill needs a backup person / plan, reviewed every <b>6 months</b>.
      </p>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hr-critical-summary">
          {[['Skills', s.skills, 'bg-white border-slate-200 text-slate-700'],
            ['Critical', s.critical, 'bg-rose-50 border-rose-200 text-rose-700'],
            ['No backup plan', s.missing_plan, 'bg-red-50 border-red-200 text-red-700'],
            ['Review overdue', s.review_overdue, 'bg-amber-50 border-amber-200 text-amber-700']].map(([l, v, c]) => (
            <div key={l} className={`rounded-lg border px-3 py-2 ${c}`}>
              <p className="text-lg font-bold leading-none">{v}</p>
              <p className="text-[10px] uppercase tracking-wide mt-1">{l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOnlyCritical(v => !v)} data-testid="hr-critical-toggle-filter">
          {onlyCritical ? 'Show all skills' : 'Show critical only'}
        </Button>
        {canManage && (
          <Button size="sm" variant="outline" className="gap-1" onClick={runCheck} data-testid="hr-critical-run-check">
            <BellRing className="w-4 h-4" /> Run 6-month review check
          </Button>
        )}
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/80">
            <TableHead className="text-xs">Skill</TableHead>
            <TableHead className="text-xs">Group</TableHead>
            <TableHead className="text-xs text-center">Persons at S2+</TableHead>
            <TableHead className="text-xs text-center">Critical</TableHead>
            <TableHead className="text-xs">Backup person / plan</TableHead>
            <TableHead className="text-xs">Review</TableHead>
            {canManage && <TableHead className="text-xs w-16 text-right">Edit</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8 text-sm">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8 text-sm">No critical skills — every skill has 2+ persons at S2 and needs no specific qualification</TableCell></TableRow>
            ) : rows.map(r => (
              <TableRow key={r.skill_id} className={r.critical && r.review_status !== 'ok' ? 'bg-red-50/30' : ''} data-testid={`hr-critical-row-${r.skill_id}`}>
                <TableCell className="text-sm font-medium">
                  {r.skill_name}
                  {r.requires_specific_qualification && (
                    <span className="block text-[10px] text-rose-600">Qualification required{r.required_qualification_name ? `: ${r.required_qualification_name}` : ''}</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{r.skill_group_name || '—'}</TableCell>
                <TableCell className="text-center text-sm font-semibold" data-testid={`hr-critical-s2-${r.skill_id}`}>{r.s2_plus_count}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={`text-[10px] ${r.critical ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`} data-testid={`hr-critical-flag-${r.skill_id}`} title={r.critical_reason}>
                    {r.critical ? 'Y' : 'N'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[280px]">
                  {r.backup_employee_names.length > 0 && <span className="font-medium text-slate-700">{r.backup_employee_names.join(', ')}</span>}
                  {r.backup_plan && <span className="block text-slate-500">{r.backup_plan}</span>}
                  {!r.backup_employee_names.length && !r.backup_plan && <span className="text-slate-400">{r.critical ? 'To be defined' : '—'}</span>}
                </TableCell>
                <TableCell className="text-xs">
                  {r.critical ? (
                    <>
                      <Badge variant="outline" className={`text-[10px] ${STATUS[r.review_status].cls}`} data-testid={`hr-critical-review-${r.skill_id}`}>{STATUS[r.review_status].label}</Badge>
                      {r.next_review_date && <span className="block text-[10px] text-slate-400 mt-0.5 font-mono">next: {r.next_review_date}</span>}
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7"
                      onClick={() => setDlg({ skill_id: r.skill_id, skill_name: r.skill_name, backup_employee_ids: [...r.backup_employee_ids], backup_plan: r.backup_plan, last_reviewed_date: '' })}
                      data-testid={`hr-critical-edit-${r.skill_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) { setDlg(null); setEmpSearch(''); } }}>
        <DialogContent className="max-w-lg" data-testid="hr-critical-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-rose-600" /> Backup plan — {dlg?.skill_name}</DialogTitle>
            <DialogDescription className="text-xs">Saving records today as the review date; the next review falls due 6 months later.</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Backup person(s)</Label>
                <Input value={empSearch} onChange={e => setEmpSearch(e.target.value)} placeholder="Search employees…" className="h-8 text-xs mt-1" data-testid="hr-critical-emp-search" />
                <div className="border rounded-md mt-1.5 max-h-40 overflow-auto divide-y">
                  {empPool.length === 0 ? <p className="text-xs text-slate-400 p-2">No employees match</p> : empPool.map(e => {
                    const on = dlg.backup_employee_ids.includes(e.id);
                    return (
                      <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" className="h-3.5 w-3.5 accent-pink-600" checked={on}
                          onChange={() => setDlg({ ...dlg, backup_employee_ids: on ? dlg.backup_employee_ids.filter(i => i !== e.id) : [...dlg.backup_employee_ids, e.id] })}
                          data-testid={`hr-critical-emp-${e.id}`} />
                        {e.name}{e.emp_code ? <span className="text-slate-400">· {e.emp_code}</span> : null}
                      </label>
                    );
                  })}
                </div>
                {dlg.backup_employee_ids.length > 0 && (
                  <p className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {dlg.backup_employee_ids.length} selected</p>
                )}
              </div>
              <div>
                <Label className="text-xs">Backup plan</Label>
                <Textarea value={dlg.backup_plan} onChange={e => setDlg({ ...dlg, backup_plan: e.target.value })}
                  placeholder="e.g. Train B. Kumar to S2 by Sept; cross-cover from CNC cell; external agency on retainer"
                  className="min-h-[90px] text-xs mt-1" data-testid="hr-critical-plan-input" />
              </div>
              <div>
                <Label className="text-xs">Reviewed on (optional — defaults to today)</Label>
                <Input type="date" value={dlg.last_reviewed_date} onChange={e => setDlg({ ...dlg, last_reviewed_date: e.target.value })}
                  className="h-8 text-xs mt-1" data-testid="hr-critical-reviewed-date" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlg(null); setEmpSearch(''); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="hr-critical-save">{saving ? 'Saving…' : 'Save & mark reviewed'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};
